import React, { useState, useEffect, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Card, Button, Modal } from "./ui";
import { formatMoney } from "../lib/utils";
import { assetMeta, assetTypeLabel } from "../lib/assets";
import AssetWizard from "./AssetWizard";
import SellAssetModal from "./SellAssetModal";
import { Plus, Pencil, Trash2, Landmark, Tag, ChevronDown, ChevronRight } from "lucide-react";

const POS = "#10B981";
const NEG = "#EF4444";

export default function AssetsCard({ entity, base, hidden, accounts, onChange, refreshKey }) {
  const { t, lang, canEdit, toast } = useApp();
  const [data, setData] = useState(null);
  const [wizard, setWizard] = useState(null); // {} new, item edit
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selling, setSelling] = useState(null);
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const money = (v, dec = 0) => (hidden ? "••••" : formatMoney(v, base, { decimals: dec }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolio/assets?entity_id=${entity.id}`);
      setData(data);
    } catch { setData(null); }
  }, [entity.id]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const doDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    try {
      await api.delete(`/portfolio/assets/${id}`);
    } catch (e) {
      if (e?.response?.status !== 404) { toast(t("assets_delete_error")); return; }
    }
    toast(t("assets_deleted"));
    load();
    onChange?.();
  };

  const items = data?.items || [];
  const assets = items.filter((i) => i.kind !== "liability");
  const liabilities = items.filter((i) => i.kind === "liability");

  const Row = (it) => {
    const meta = assetMeta(it);
    const Ico = meta.icon;
    const isLiab = it.kind === "liability";
    const sold = it.is_closed;
    const sales = it.sales || [];
    const hasLog = sales.length > 0;
    const isOpen = !!expanded[it.id];
    return (
      <div key={it.id} className={`rounded-xl border ${sold ? "opacity-60" : ""}`} data-testid={`asset-${it.id}`}>
        <div className="px-3.5 py-3 flex items-center gap-3 group">
          {hasLog ? (
            <button onClick={() => toggle(it.id)} data-testid={`asset-toggle-log-${it.id}`} className="shrink-0 text-text-secondary hover:text-text-primary">
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : <span className="w-4 shrink-0" />}
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}1f` }}>
            <Ico className="w-4 h-4" style={{ color: meta.color }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{it.name}</div>
            <div className="text-[11px] text-text-secondary truncate flex items-center gap-1.5">
              <span>{assetTypeLabel(it, lang)}</span>
              {sold && <span className="px-1.5 py-0.5 rounded-full border text-[10px]">{it.kind === "liability" ? t("assets_closed") : t("assets_sold")}</span>}
              {!sold && it.is_live && <span className="px-1.5 py-0.5 rounded-full border text-[10px]">{t("assets_live")}</span>}
              {!sold && hasLog && it.realized_cumulative != null && it.realized_cumulative !== 0 && (
                <span className="text-[10px]" style={{ color: it.realized_cumulative >= 0 ? POS : NEG }}>
                  {t("assets_realized_gain")} {it.realized_cumulative >= 0 ? "+" : "−"}{formatMoney(Math.abs(it.realized_cumulative), it.currency, { decimals: 0 })}
                </span>
              )}
              {it.currency && it.currency !== base && <span className="font-mono">{it.currency}</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            {sold ? (
              it.realized_gain != null ? (
                <div className="text-sm font-semibold tabular-nums" style={{ color: it.realized_gain >= 0 ? POS : NEG }} data-testid={`asset-realized-${it.id}`}>
                  {it.realized_gain >= 0 ? "+" : "−"}{formatMoney(Math.abs(it.realized_gain), it.currency, { decimals: 0 })}
                  <span className="block text-[10px] text-text-secondary font-normal">{t("assets_realized_gain")}</span>
                </div>
              ) : <span className="text-[11px] text-text-secondary">{t("assets_closed")}</span>
            ) : (
              <>
                <div className="text-sm font-semibold tabular-nums" style={{ color: isLiab ? NEG : undefined }}>
                  {isLiab ? "−" : ""}{money(it.value_base, 0)}
                </div>
                {it.gain != null && (
                  <div className="text-[10px]" style={{ color: it.gain >= 0 ? POS : NEG }}>
                    {t("portfolio_gain")} {it.gain >= 0 ? "+" : ""}{formatMoney(it.gain, it.native_currency, { decimals: 0 })}
                  </div>
                )}
              </>
            )}
          </div>
          {canEdit && !sold && (
            <div className="flex items-center gap-2 shrink-0">
              {!isLiab && (
                <button onClick={() => setSelling(it)} data-testid={`asset-sell-${it.id}`} title={t("assets_sell")} className="text-text-secondary hover:text-emerald-400"><Tag className="w-3.5 h-3.5" /></button>
              )}
              <button onClick={() => setWizard(it)} data-testid={`asset-edit-${it.id}`} className="text-text-secondary hover:text-text-primary"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => setPendingDelete(it)} data-testid={`asset-delete-${it.id}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {canEdit && sold && (
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setPendingDelete(it)} data-testid={`asset-delete-${it.id}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
        {hasLog && isOpen && (
          <div className="px-3.5 pb-3 pt-0 space-y-1" data-testid={`asset-sales-log-${it.id}`}>
            <div className="text-[10px] uppercase tracking-wide text-text-secondary pl-7 mb-1">{t("assets_sales_log")}</div>
            {sales.map((srec, i) => (
              <div key={i} className="pl-7 flex items-center gap-2 text-[11px] text-text-secondary" data-testid={`asset-sale-${it.id}-${i}`}>
                <span className="w-20 shrink-0 tabular-nums">{srec.date}</span>
                <span className="flex-1 truncate">{srec.quantity} oz × {formatMoney(srec.price, it.currency, { decimals: 2 })}</span>
                <span className="tabular-nums">{formatMoney(srec.proceeds, it.currency, { decimals: 0 })}</span>
                {srec.realized_gain != null && (
                  <span className="w-20 text-right tabular-nums" style={{ color: srec.realized_gain >= 0 ? POS : NEG }}>
                    {srec.realized_gain >= 0 ? "+" : "−"}{formatMoney(Math.abs(srec.realized_gain), it.currency, { decimals: 0 })}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="mt-5" data-testid="assets-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-semibold">{t("assets_title")}</h3>
          {data && (
            <span className="text-xs text-text-secondary" data-testid="assets-net">
              {t("assets_net")} {money(data.net_assets, 0)}
            </span>
          )}
          {data && !!data.realized_total && (
            <span className="text-xs" style={{ color: data.realized_total >= 0 ? POS : NEG }} data-testid="assets-realized-total">
              {t("assets_realized_total")} {data.realized_total >= 0 ? "+" : "−"}{money(Math.abs(data.realized_total), 0)}
            </span>
          )}
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => setWizard({})} data-testid="add-asset-button">
            <Plus className="w-4 h-4 mr-1" /> {t("assets_add")}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-text-secondary py-4">{t("assets_none")}</p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-secondary mb-2">
              {t("assets_assets")} · {money(data.assets_total, 0)}
            </div>
            <div className="space-y-2">
              {assets.length ? assets.map(Row) : <p className="text-sm text-text-secondary py-2">{t("assets_none_assets")}</p>}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wide text-text-secondary mb-2">
              {t("assets_liabilities")} · {money(data.liabilities_total, 0)}
            </div>
            <div className="space-y-2">
              {liabilities.length ? liabilities.map(Row) : <p className="text-sm text-text-secondary py-2">{t("assets_none_liabilities")}</p>}
            </div>
          </div>
        </div>
      )}

      {wizard && (
        <AssetWizard entity={entity} item={wizard.id ? wizard : null}
          onClose={() => setWizard(null)}
          onSaved={() => { setWizard(null); load(); onChange?.(); }} />
      )}

      {pendingDelete && (
        <Modal open onClose={() => setPendingDelete(null)} title={pendingDelete.name}
          footer={<>
            <Button variant="outline" onClick={() => setPendingDelete(null)} data-testid="asset-delete-cancel">{t("cancel")}</Button>
            <Button onClick={doDelete} className="!bg-red-500 hover:!bg-red-600" data-testid="asset-delete-confirm">{t("delete")}</Button>
          </>}>
          <p className="text-sm text-text-secondary">{t("assets_delete_confirm")}</p>
        </Modal>
      )}
      {selling && (
        <SellAssetModal asset={selling} items={items} accounts={accounts}
          onClose={() => setSelling(null)}
          onSold={() => { setSelling(null); load(); onChange?.(); }} />
      )}
    </Card>
  );
}
